
import { useId, useMemo, useRef, useState } from 'react';
import { Field, Stack, Heading, Text, Badge } from '@client/shared/design';
import { firstFieldError } from '@client/shared/form';
import { BookingAccessKind, type BookingAccessInput } from '@shared/listing';
import { cx } from '@client/shared/lib/cx.ts';
import { RadioCard } from './RadioCard.tsx';
import type { NewListingFormApi } from './use-new-listing-form.ts';
import styles from './NewListing.module.css';

export interface AgentOption {
  id: string;
  name: string;
  email: string;
  roles: readonly string[];
}

export interface StepAccessProps {
  form: NewListingFormApi;
  agents: readonly AgentOption[];
}

const toggleId = (ids: readonly string[], id: string): string[] =>
  ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];

export function StepAccess({ form, agents }: StepAccessProps) {
  const hasRoster = agents.length > 0;

  return (
    <Stack gap={7}>
      <Stack gap={1}>
        <Heading level={3}>Who gets access?</Heading>
        <Text tone="soft">
          By default every agent in your group can book this listing. Narrow it if only certain
          agents should, and choose who is notified about updates.
        </Text>
      </Stack>

      <form.Field name="bookingAccess">
        {(field) => {
          const access = field.state.value;
          const setKind = (kind: BookingAccessInput['kind']) => {
            if (kind === BookingAccessKind.AllAgents) {
              field.handleChange({ kind: BookingAccessKind.AllAgents });
            } else {
              const userIds =
                access.kind === BookingAccessKind.SpecificAgents ? access.userIds : [];
              field.handleChange({ kind: BookingAccessKind.SpecificAgents, userIds });
            }
          };
          const selectedIds =
            access.kind === BookingAccessKind.SpecificAgents ? access.userIds : [];
          const toggle = (id: string) =>
            field.handleChange({
              kind: BookingAccessKind.SpecificAgents,
              userIds: toggleId(selectedIds, id),
            });

          return (
            <Field label="Who can book this listing" error={firstFieldError(field.state.meta.errors)}>
              {() => (
                <Stack gap={3}>
                  <RadioCard
                    name="bookingAccess"
                    checked={access.kind === BookingAccessKind.AllAgents}
                    onChange={() => setKind(BookingAccessKind.AllAgents)}
                    title="All agents"
                    desc="Everyone in your group can book an open house here."
                  />
                  <RadioCard
                    name="bookingAccess"
                    checked={access.kind === BookingAccessKind.SpecificAgents}
                    onChange={() => setKind(BookingAccessKind.SpecificAgents)}
                    disabled={!hasRoster}
                    title="Specific agents"
                    desc={
                      hasRoster
                        ? 'Only the agents you choose can book.'
                        : 'Available once your group roster loads.'
                    }
                  />

                  {access.kind === BookingAccessKind.SpecificAgents && hasRoster && (
                    <AgentSelect
                      agents={agents}
                      selected={selectedIds}
                      onToggle={toggle}
                      emptyHint="Add at least one agent, or switch back to all agents."
                    />
                  )}
                </Stack>
              )}
            </Field>
          );
        }}
      </form.Field>

      <form.Field name="notifyUserIds">
        {(field) => {
          const selected = field.state.value;
          const toggle = (id: string) => field.handleChange(toggleId(selected, id));
          return (
            <Field
              label="Who gets notified when a slot is booked"
              optional
              hint="These people receive an email whenever an agent books or cancels an open house here."
              error={firstFieldError(field.state.meta.errors)}
            >
              {() =>
                hasRoster ? (
                  <AgentSelect agents={agents} selected={selected} onToggle={toggle} />
                ) : (
                  <Text size="sm" tone="muted">
                    Notification recipients can be chosen once your group roster loads. You can add them later by editing the listing.
                  </Text>
                )
              }
            </Field>
          );
        }}
      </form.Field>
    </Stack>
  );
}

function AgentSelect({
  agents,
  selected,
  onToggle,
  emptyHint,
}: {
  agents: readonly AgentOption[];
  selected: readonly string[];
  onToggle: (id: string) => void;
  emptyHint?: string;
}) {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  const byId = useMemo(() => new Map(agents.map((a) => [a.id, a])), [agents]);
  const selectedAgents = selected.map((id) => byId.get(id)).filter((a): a is AgentOption => !!a);

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    const chosen = new Set(selected);
    return agents.filter(
      (a) =>
        !chosen.has(a.id) &&
        (q === '' || a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q)),
    );
  }, [agents, selected, q]);

  const visible = open && matches.length > 0;

  const add = (id: string) => {
    onToggle(id);
    setQuery('');
    setActive(-1);
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && query === '' && selectedAgents.length > 0) {
      onToggle(selectedAgents[selectedAgents.length - 1]!.id);
      return;
    }
    // Enter here selects a match or does nothing — it must never submit the wizard
    // form (access is the last step, so a bare Enter would save the listing).
    if (e.key === 'Enter') {
      e.preventDefault();
      if (visible) {
        const chosen = active >= 0 ? matches[active] : matches[0];
        if (chosen) add(chosen.id);
      }
      return;
    }
    if (!visible) {
      if (e.key === 'ArrowDown') setOpen(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <Stack gap={2}>
      <div className={styles.autocomplete}>
        <div
          className={styles.tokenField}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) inputRef.current?.focus();
          }}
        >
          {selectedAgents.map((a) => (
            <span key={a.id} className={styles.chip}>
              <span className={styles.chipName}>{a.name}</span>
              <button
                type="button"
                className={styles.chipRemove}
                aria-label={`Remove ${a.name}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onToggle(a.id);
                  inputRef.current?.focus();
                }}
              >
                <RemoveMark />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            className={styles.tokenInput}
            type="text"
            role="combobox"
            aria-expanded={visible}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={visible && active >= 0 ? `${listId}-opt-${active}` : undefined}
            autoComplete="off"
            placeholder={selectedAgents.length ? 'Add another…' : 'Search agents…'}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              setActive(-1);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            onBlur={() => setTimeout(() => setOpen(false), 120)}
          />
        </div>

        {visible && (
          <ul id={listId} role="listbox" className={styles.suggestions}>
            {matches.map((a, i) => (
              <li
                key={a.id}
                id={`${listId}-opt-${i}`}
                role="option"
                aria-selected={i === active}
                className={cx(
                  styles.suggestion,
                  styles.agentSuggestion,
                  i === active && styles.suggestionActive,
                )}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  add(a.id);
                }}
              >
                <span className={styles.agentMeta}>
                  <span className={styles.agentName}>{a.name}</span>
                  <span className={styles.agentEmail}>{a.email}</span>
                </span>
                <span className={styles.agentRoles}>
                  {a.roles.map((r) => (
                    <Badge key={r} tone="neutral">
                      {r}
                    </Badge>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {emptyHint && selected.length === 0 && (
        <Text size="xs" tone="muted">
          {emptyHint}
        </Text>
      )}
    </Stack>
  );
}

function RemoveMark() {
  return (
    <svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M4 4l8 8M12 4l-8 8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
